import { lazy, Suspense } from "react"

import { Skeleton } from "@/atoms"
import useServices from "@/hooks/useServices"
import { TAB_NAMES, WIDTH_DEFAULT } from "@/organisms/Drawers/const"
import { TabsDrawerTemplate } from "../TabsDrawerTemplate"
import { AttackSimulationTab } from "./components/AttackSimulationTab"
import { ExploitabilityTabTriageDrawer } from "./components/ExploitabilityTabTriageDrawer"
import { ExtraTriageDrawer } from "./components/ExtraTriageDrawer"
import { OverviewTabTriageDrawer } from "./components/OverviewTabTriageDrawer"
import { RemediationTabTriageDrawer } from "./components/RemediationTabTriageDrawer"
import { RuntimeEvidenceTabTriageDrawer } from "./components/RuntimeEvidenceTabTriageDrawer"
import { TitleTriageDrawer } from "./components/TitleTriageDrawer"
import { INITIAL_STATE } from "./const"
import { useIsEvpDemo } from "./hooks/useIsEvpDemo"

import styles from "./TriageDrawer.module.scss"

import type { TriageDrawerComponent } from "."
import type { TabComponentProps } from "../TabsDrawerTemplate"

// Demo-only, so it is lazy for the same reason the EVP tabs are (they arrive
// already wrapped by makeLazyEvpTab). No availability gate here — unlike those,
// the Overview tab is always selectable.
const DemoOverviewTabView = lazy(() =>
  import("./components/DemoOverviewTab").then((module) => ({
    default: module.DemoOverviewTab
  }))
)

const DemoOverviewTab = (props: TabComponentProps) => (
  <Suspense fallback={<Skeleton active />}>
    <DemoOverviewTabView {...props} />
  </Suspense>
)

export const TriageDrawer: TriageDrawerComponent = (props) => {
  const { auth } = useServices()
  const attackScenariosTabName = TAB_NAMES.attackScenarios
  const runtimeEvidenceTabName = TAB_NAMES.runtimeEvidence
  const exploitabilityEnabled = useIsEvpDemo()
  // Attack Scenarios is an internal-tenant tab, hidden in the EVP demo.
  const attackScenariosEnabled =
    auth.isInternalTenant() && !exploitabilityEnabled
  // `tabs` and `components` are index-aligned — keep them in step.
  const tabs = [
    "overview",
    ...(exploitabilityEnabled ? [TAB_NAMES.remediation] : []),
    runtimeEvidenceTabName,
    ...(exploitabilityEnabled ? [TAB_NAMES.exploitValidation] : []),
    ...(attackScenariosEnabled ? [attackScenariosTabName] : [])
  ]
  const components = [
    exploitabilityEnabled ? DemoOverviewTab : OverviewTabTriageDrawer,
    ...(exploitabilityEnabled ? [RemediationTabTriageDrawer] : []),
    RuntimeEvidenceTabTriageDrawer,
    ...(exploitabilityEnabled ? [ExploitabilityTabTriageDrawer] : []),
    ...(attackScenariosEnabled ? [AttackSimulationTab] : [])
  ]
  const { widthAdditive, ...restProps } = props

  return (
    <TabsDrawerTemplate
      name="TriageDrawer"
      tabs={tabs}
      initialState={INITIAL_STATE}
      components={components}
      width={WIDTH_DEFAULT + widthAdditive}
      Title={TitleTriageDrawer}
      classes={{ drawer: styles.drawer }}
      controlledActiveKey
      Extra={ExtraTriageDrawer}
      {...restProps}
    />
  )
}
